// eslint-disable-next-line @typescript-eslint/ban-ts-comment
import { LoadingState } from '@/_models/virtualStaging/VSObject';
import { createHTMLMesh } from '@/_models/virtualStaging/HTMLMesh/HTMLMesh';
import { InteractiveGroup } from '@/_models/virtualStaging/HTMLMesh/utils/InteractiveGroup';
import { AbstractHTMLMesh } from '@/_models/virtualStaging/HTMLMesh/component/AbstractHTMLMesh';
import { ComponentInteractionType } from '@/_models/virtualStaging/Scene.component';
import {
  getRelativePaddingLeft,
  getRelativePaddingTop,
} from '@/_models/virtualStaging/HTMLMesh/utils/getRelativePaddingTop';

export class PathDestinationMeshComponent extends AbstractHTMLMesh {
  private hostPose;

  public popupMesh;

  public mediaMesh;

  public refs: {};

  public tag;

  public textureLoader;

  public vec3helper1;

  public vec3helper2;

  inputs: any = {
    html: null,
    media: null,
    dimensions: { width: 400, height: 400 },
    scale: 3,
    visible: true,
    popupVisible: true,
    popupScale: 16,
    sweep: null,
  };

  events = {
    [ComponentInteractionType.CLICK]: true,
    [ComponentInteractionType.HOVER]: true,
    [ComponentInteractionType.DRAG]: false,
  };

  onInit() {
    const THREE = this.context.three;
    const { html, media } = this.inputs;
    this.vec3helper1 = new THREE.Vector3();
    this.vec3helper2 = new THREE.Vector3();
    this.initRoot();
    const { x, y, z } = this.inputs.sweep.position;
    this.root.position.set(x, y, z);
    this.textureLoader = new THREE.TextureLoader();

    this.initPopupMesh(html);
    if (media) this.initMedia(media);
    this.initTriangle();
    this.addMeshPointerEvents();

    this.outputs.loadingState = LoadingState.Loaded;
  }

  initTriangle() {
    const THREE = this.context.three;
    const shape = new THREE.Shape();
    shape.moveTo(0, -1);
    shape.lineTo(1, 0);
    shape.lineTo(-1, 0);
    shape.lineTo(0, -1);
    const geometry = new THREE.ShapeGeometry(shape);
    const material = new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.DoubleSide });

    const triangle = new THREE.Mesh(geometry, material);
    triangle.position.y = -0.055;
    triangle.position.z = 0.01;
    triangle.scale.setScalar(0.03);

    this.pivot.add(triangle);
  }

  initPopupMesh(html) {
    this.popupMesh = createHTMLMesh(html, this.context.three);
    this.popupMesh.material.side = this.context.three.DoubleSide;
    this.popupMesh.userData = { isPointerOver: false };
  }

  initMedia(media) {
    const THREE = this.context.three;
    this.textureLoader.load(media, (texture) => {
      const texWidth = texture.image.width;
      const texHeight = texture.image.height;

      // Calculate the aspect ratio of the texture
      const aspectRatio = texWidth / texHeight;

      // Set the width and height of the mesh
      const meshWidth = 0.1;
      const meshHeight = 0.1 / aspectRatio;

      // Create the plane geometry and mesh
      const geometry = new THREE.PlaneGeometry(meshWidth, meshHeight);
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        side: THREE.DoubleSide,
        blending: THREE.NormalBlending,
        transparent: true,
      });

      this.mediaMesh = new THREE.Mesh(geometry, material);
      this.mediaMesh.position.z = 0.01;

      const offset = getRelativePaddingTop(this.inputs.html);
      const offsetLeft = getRelativePaddingLeft(this.inputs.html);
      this.mediaMesh.position.y =
        (this.popupMesh.geometry.parameters.height - this.mediaMesh.geometry.parameters.height) *
          0.5 -
        offset * this.popupMesh.geometry.parameters.height;
      this.mediaMesh.position.x =
        (-this.popupMesh.geometry.parameters.width + this.mediaMesh.geometry.parameters.width) *
          0.5 +
        offsetLeft * this.popupMesh.geometry.parameters.width;
      this.popupMesh.add(this.mediaMesh);
    });
  }

  addMeshPointerEvents() {
    const group = new InteractiveGroup(this.context.renderer, this.context.camera, this.context);
    this.pivot.add(group);
    group.add(this.popupMesh);
  }

  setHostPose(pose) {
    this.hostPose = pose;
  }

  setPositionFromPose(pose) {
    this.root.lookAt(pose.position.x, pose.position.y - 0.1, pose.position.z);
  }

  setFromHostPose() {
    this.setPositionFromPose(this.hostPose);
  }

  cycleVR(_session) {}

  lookAtHost() {
    this.root.lookAt(this.hostPose.position.x, this.hostPose.position.y, this.hostPose.position.z);
    this.popupMesh.lookAt(
      this.hostPose.position.x,
      this.hostPose.position.y,
      this.hostPose.position.z,
    );
  }

  setVisible(visible) {
    this.root.visible = visible;
  }

  onInputsUpdated(previousInputs: this['inputs']) {}

  onTick() {
    if (this.hostPose) this.lookAtHost();
    this.context.camera.getWorldPosition(this.vec3helper1);
    this.pivot.getWorldPosition(this.vec3helper2);
    if (this.vec3helper1.distanceTo(this.vec3helper2) < 1) this.root.visible = false;

    if (this.context?.renderer?.xr?.isPresenting) {
      const _session = this.context.renderer.xr.getSession();
      _session.requestAnimationFrame(() => this.cycleVR(_session));
    }
  }
}

export const pathDestinationMeshComponentType = 'mp.pathDestinationMeshComponent';
export const makePathDestinationMeshComponent = (): any => new PathDestinationMeshComponent();
